import { useMemo } from 'react';
import useDesignTokens from '@assetron/web-common/themes/useDesignTokens';
import { UseFeaturedIconStylesProps } from './types';

// Helper function to get color configurations
const getVariantColors = (
  variant: string,
  isModern: boolean,
  tokens: ReturnType<typeof useDesignTokens>,
) => {
  const colorMap: Record<string, { background: string; border: string }> = {
    Brand: {
      background: tokens.colors.bg.brand.primary,
      border: tokens.colors.bg.brand.secondary,
    },
    Gray: {
      background: isModern
        ? tokens.colors.bg.primaryInverse
        : tokens.colors.bg.tertiary,
      border: isModern
        ? tokens.colors.bg.primaryInverse
        : tokens.colors.border.primary,
    },
    Error: {
      background: isModern
        ? tokens.colors.bg.error.solid
        : tokens.colors.bg.primary,
      border: isModern
        ? tokens.colors.bg.error.solid
        : tokens.colors.bg.secondary,
    },
    Warning: {
      background: isModern
        ? tokens.colors.bg.warning.solid
        : tokens.colors.bg.warning.primary,
      border: isModern
        ? tokens.colors.bg.warning.solid
        : tokens.colors.bg.warning.secondary,
    },
    Success: {
      background: isModern
        ? tokens.colors.bg.success.solid
        : tokens.colors.bg.success.primary,
      border: isModern
        ? tokens.colors.bg.success.solid
        : tokens.colors.bg.success.secondary,
    },
  };

  return colorMap[variant] || colorMap.Brand;
};

// Helper function to get box shadow configurations
const getBoxShadowConfig = (
  variant: string,
  tokens: ReturnType<typeof useDesignTokens>,
) => {
  const shadowMap: Record<string, string> = {
    Brand: `0 12px 16px -4px ${tokens.colors.bg.brand.solidPrimary}52, 0 4px 6px -2px ${tokens.colors.bg.brand.solidPrimary}3d`,
    Success: `0 12px 16px -4px ${tokens.colors.bg.success.solid}52, 0 4px 6px -2px ${tokens.colors.bg.success.secondary}3d`,
    Warning: `0 12px 16px -4px ${tokens.colors.bg.warning.solid}52, 0 4px 6px -2px ${tokens.colors.bg.warning.secondary}3d`,
    Error: `0 12px 16px -4px ${tokens.colors.bg.error.solid}52, 0 4px 6px -2px ${tokens.colors.bg.error.secondary}3d`,
  };

  return shadowMap[variant];
};

const useFeaturedIconStyles = ({
  size,
  variant,
  type,
}: UseFeaturedIconStylesProps) => {
  const tokens = useDesignTokens();

  const styles = useMemo(() => {
    // Size configurations
    const sizeConfigs = {
      sm: { containerSize: 32, iconSize: 16 },
      md: { containerSize: 40, iconSize: 20 },
      lg: { containerSize: 48, iconSize: 24 },
      xl: { containerSize: 56, iconSize: 28 },
    };

    const isModern = type === 'Modern';
    const boxShadow = isModern
      ? getBoxShadowConfig(variant, tokens)
      : undefined;
    const currentSize = sizeConfigs[size];
    const colors = getVariantColors(variant, isModern, tokens);

    return {
      container: {
        position: 'relative',
        width: currentSize.containerSize,
        height: currentSize.containerSize,
        borderRadius: tokens.radius.full || '50%',
        backgroundColor: colors.background,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow,
      },
      iconContainer: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: currentSize.iconSize,
        height: currentSize.iconSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        zIndex: 2,
      },
      border: {
        position: 'absolute',
        inset: 0,
        borderRadius: tokens.radius.full || '50%',
        border: `1px solid ${colors.border}`,
        pointerEvents: 'none',
        zIndex: 1,
      },
    };
  }, [size, variant, type, tokens]);

  return styles;
};

export default useFeaturedIconStyles;
